import { createReducer, combineReducers } from "@reduxjs/toolkit";
import * as actionTypes from "~/store/action_types";
import {
  ADD_CALENDAR_EVENT_SUCCESS,
  EDIT_CALENDAR_EVENT_SUCCESS,
  FETCH_CALENDAR_EVENT_SUCCESS,
} from "~/store/action_types";

const PARENT_TYPES = {
  ProjectTemplateTask: "project-template-task",
  ProjectTemplateRecurringTask: "project-template-recurring-task",
  ProjectTemplateNote: "project-template-note",
  ProjectTemplateDiscussion: "project-template-discussion",
};

const invoices = createReducer({}, (builder) => {
  builder.addCase(actionTypes.FETCH_ALL_INVOICES_SUCCESS, (state, action) => {
    if (action.response && action.response.entities && action.response.entities.subscription_invoices) {
      return action.response.entities.subscription_invoices;
    }
    return state;
  });
});

const invoicesIds = createReducer([], (builder) => {
  builder.addCase(actionTypes.FETCH_ALL_INVOICES_SUCCESS, (state, action) => {
    if (action.response && action.response.result) {
      return action.response.result;
    }
    return state;
  });
});

const tasks = createReducer({}, (builder) => {
  builder
    .addCase(actionTypes.ADD_COMMENT_SUCCESS, (state, action) => {
      if (action.parentType === "Task") {
        const { response = {} } = action;
        const { entities = {}, result = {} } = response;
        const { comments = {} } = entities;
        const { single } = result;
        const task = state[action.parentId] ?? [];
        const ids = new Set([...action.mentionedIds, ...task, comments[single].created_by_id]);
        state[action.parentId] = [...ids];
      }
      return state;
    })
    .addMatcher(
      (action) =>
        [
          actionTypes.LOAD_TASK,
          actionTypes.EDIT_TASK_SUCCESS,
          actionTypes.UPDATE_TASK_FROM_EVENT_STREAM,
          actionTypes.ANGULAR_INIT_TASK_AND_COMMENTS_MODAL,
        ].includes(action.type),
      (state, action) => {
        const { response = {}, taskId } = action;
        const { result = {} } = response;
        if (result.subscribers && taskId) {
          state[taskId] = result.subscribers;
        }
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.UPDATE_SUBSCRIBERS_SUCCESS,
          actionTypes.REMOVE_SUBSCRIBER_SUCCESS,
          actionTypes.ADD_SUBSCRIBER_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { parentType, parentId, response = {} } = action;

        if (parentType === "task" && parentId && response) {
          state[parentId] = response;
        }
        return state;
      }
    );
});

const recurringTasks = createReducer({}, (builder) => {
  builder.addCase(actionTypes.FETCH_RECURRING_TASK_SUCCESS, (state, action) => {
    if (action.response && action.response.result) {
      state = {
        ...state,
        [action.recurringTaskId]: action.response.result.subscribers,
      };
    }
    return state;
  });
});

const notes = createReducer({}, (builder) => {
  builder
    .addMatcher(
      (action) =>
        [
          actionTypes.FETCH_NOTE_SUCCESS,
          actionTypes.PREFETCH_NOTE_SUCCESS,
          actionTypes.ADD_NOTE_SUCCESS,
          actionTypes.EDIT_NOTE_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { response = {} } = action;
        const { result = {} } = response;
        const { single, subscribers = [] } = result;
        if (single) {
          state = {
            ...state,
            [single]: subscribers,
          };
        }
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.UPDATE_SUBSCRIBERS_SUCCESS,
          actionTypes.REMOVE_SUBSCRIBER_SUCCESS,
          actionTypes.ADD_SUBSCRIBER_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { response = [], parentId, parentType } = action;
        if (parentId && parentType === "note" && Array.isArray(response)) {
          state = {
            ...state,
            [parentId]: response,
          };
        }
        return state;
      }
    );
});

const discussions = createReducer({}, (builder) => {
  builder
    .addMatcher(
      (action) =>
        [
          actionTypes.FETCH_DISCUSSION_SUCCESS,
          actionTypes.PREFETCH_DISCUSSION_SUCCESS,
          actionTypes.ADD_DISCUSSION_SUCCESS,
          actionTypes.EDIT_DISCUSSION_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { response = {} } = action;
        const { result = {} } = response;
        const { single, subscribers = [] } = result;
        if (single) {
          state = {
            ...state,
            [single]: subscribers,
          };
        }
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.UPDATE_SUBSCRIBERS_SUCCESS,
          actionTypes.REMOVE_SUBSCRIBER_SUCCESS,
          actionTypes.ADD_SUBSCRIBER_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { response = [], parentId, parentType } = action;
        if (parentId && parentType === "discussion" && Array.isArray(response)) {
          state = {
            ...state,
            [parentId]: response,
          };
        }
        return state;
      }
    );
});

const priceBrakeDown = createReducer({}, (builder) => {
  builder.addCase(actionTypes.FETCH_BRAKE_DOWN_PRICE_SUCCESS, (state, action) => {
    if (!action.response) {
      return state;
    }

    return action.response;
  });
});

const calendarEvents = createReducer({}, (builder) => {
  builder
    .addCase(actionTypes.ADD_COMMENT_SUCCESS, (state, action) => {
      if (action.parentType === "CalendarEvent") {
        const { response = {} } = action;
        const { entities = {}, result = {} } = response;
        const { comments = {} } = entities;
        const { single } = result;
        const calendarEvent = state[action.parentId] ?? [];
        const ids = new Set([...action.mentionedIds, ...calendarEvent, comments[single].created_by_id]);
        state[action.parentId] = [...ids];
      }
      return state;
    })
    .addMatcher(
      (action) =>
        [FETCH_CALENDAR_EVENT_SUCCESS, EDIT_CALENDAR_EVENT_SUCCESS, ADD_CALENDAR_EVENT_SUCCESS].includes(action.type),
      (state, action) => {
        const { id, response = {} } = action;
        const { result = {} } = response;
        const { subscribers = [] } = result;
        return {
          ...state,
          [id]: subscribers,
        };
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.UPDATE_SUBSCRIBERS_SUCCESS,
          actionTypes.REMOVE_SUBSCRIBER_SUCCESS,
          actionTypes.ADD_SUBSCRIBER_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { response = [], parentId, parentType } = action;
        if (parentId && parentType === "calendar-event" && Array.isArray(response)) {
          state = {
            ...state,
            [parentId]: response,
          };
        }
        return state;
      }
    );
});

const projectTemplateElementsReducer = (elementType) =>
  createReducer({}, (builder) => {
    builder
      .addCase(actionTypes.DELETE_PROJECT_TEMPLATE_ELEMENT_SUCCESS, (state, action) => {
        const { id, elementType: type } = action;
        if (elementType === type && state[id]) {
          delete state[id];
        }
        return state;
      })
      .addMatcher(
        (action) =>
          [
            actionTypes.FETCH_PROJECT_TEMPLATE_ELEMENT_SUCCESS,
            actionTypes.ADD_PROJECT_TEMPLATE_ELEMENT_SUCCESS,
            actionTypes.EDIT_PROJECT_TEMPLATE_ELEMENT_SUCCESS,
          ].includes(action.type),
        (state, action) => {
          const { response = {} } = action;
          const { entities = {}, result = {} } = response;
          const { single: elementId, subscribers = [] } = result;
          const { projectTemplateElements } = entities;
          const element = projectTemplateElements[elementId];
          if (element.class === elementType && subscribers.length) {
            state[elementId] = subscribers;
          }
          return state;
        }
      )
      .addMatcher(
        (action) =>
          [
            actionTypes.UPDATE_SUBSCRIBERS_SUCCESS,
            actionTypes.REMOVE_SUBSCRIBER_SUCCESS,
            actionTypes.ADD_SUBSCRIBER_SUCCESS,
          ].includes(action.type),
        (state, action) => {
          const { response = [], parentId, parentType } = action;
          if (parentId && parentType === PARENT_TYPES[elementType] && Array.isArray(response)) {
            state = {
              ...state,
              [parentId]: response,
            };
          }
          return state;
        }
      );
  });

const projectTemplateTasks = projectTemplateElementsReducer("ProjectTemplateTask");
const projectTemplateRecurringTasks = projectTemplateElementsReducer("ProjectTemplateRecurringTask");
const projectTemplateNotes = projectTemplateElementsReducer("ProjectTemplateNote");
const projectTemplateDiscussions = projectTemplateElementsReducer("ProjectTemplateDiscussion");

export default combineReducers({
  invoices,
  invoicesIds,
  tasks,
  recurringTasks,
  notes,
  discussions,
  priceBrakeDown,
  calendarEvents,
  projectTemplateTasks,
  projectTemplateRecurringTasks,
  projectTemplateNotes,
  projectTemplateDiscussions,
});
